// src\contexts\AuthContext.jsx
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import {
  auth,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  getCurrentUserToken,
  getCurrentUserClaims,
  refreshToken,
} from '../services/firebase';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../services/firebase';

const AuthContext = createContext();

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth Cache for token and user data
class AuthCache {
  constructor() {
    this.cache = new Map();
    this.duration = 5 * 60 * 1000; // 5 minutes
  }

  get(key) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.duration) {
      return cached.data;
    }
    this.cache.delete(key);
    return null;
  }

  set(key, data) {
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  clear(key) {
    if (key) this.cache.delete(key);
    else this.cache.clear();
  }
}

const authCache = new AuthCache();

// Default admin user data structure
const getDefaultAdminData = (user, hasCustomClaim = false) => ({
  fullName: user.displayName || 'Admin',
  email: user.email || '',
  uid: user.uid,
  profilePictureURL: user.photoURL || '',
  createdAt: serverTimestamp(),
  lastLogin: serverTimestamp(),
  lastTokenRefresh: serverTimestamp(),
  role: 'admin',
  status: 'active',
  hasCustomClaim: hasCustomClaim,
  provider: ['email/password'],
  isAdmin: true,
  permissions: ['read:all', 'write:all', 'delete:all', 'manage:users'],
});

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authToken, setAuthToken] = useState(null);
  const [customClaims, setCustomClaims] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authInitialized, setAuthInitialized] = useState(false);

  const API_BASE_URL = '/api';

  const tokenRefreshIntervalRef = useRef(null);
  const lastTokenRefreshRef = useRef(Date.now());
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (tokenRefreshIntervalRef.current) {
        clearInterval(tokenRefreshIntervalRef.current);
      }
    };
  }, []);

  /**
   * Verify admin status with backend
   */
  const verifyAdminWithBackend = useCallback(async token => {
    try {
      const response = await fetch(`${API_BASE_URL}/verify-admin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      return data.valid === true;
    } catch (error) {
      console.error('Backend admin verification failed:', error);
      return false;
    }
  }, []);

  /**
   * Get ID token for authenticated requests
   */
  const getIdToken = useCallback(async (forceRefresh = false) => {
    try {
      if (!auth?.currentUser) return null;

      // Check cache first
      if (!forceRefresh) {
        const cached = authCache.get('authToken');
        if (cached) {
          setAuthToken(cached);
          return cached;
        }
      }

      let token;
      if (forceRefresh) {
        token = await refreshToken();
      } else {
        token = await getCurrentUserToken();
      }

      if (token) {
        setAuthToken(token);
        authCache.set('authToken', token);
      }
      return token;
    } catch (error) {
      console.error('Error getting ID token:', error);
      return null;
    }
  }, []);

  // In AuthContext.jsx, add this to the value object:
  const getAuthToken = useCallback(async () => {
    return await getIdToken();
  }, [getIdToken]);

  /**
   * Refresh token and claims periodically
   */
  const refreshAuthState = useCallback(async () => {
    if (!auth?.currentUser || !isMountedRef.current) return;

    try {
      const token = await getIdToken(true);
      if (token && isMountedRef.current) {
        const claims = await getCurrentUserClaims();
        setCustomClaims(claims);
        setIsAdmin(claims?.admin === true);
        authCache.set('customClaims', claims);

        const userRef = doc(db, 'users', auth.currentUser.uid);
        await updateDoc(userRef, {
          lastTokenRefresh: serverTimestamp(),
        }).catch(() => {});
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
    }
  }, [getIdToken]);

  /**
   * Get user data from Firestore
   */
  const fetchUserData = useCallback(async uid => {
    try {
      // Check cache
      const cached = authCache.get(`userData_${uid}`);
      if (cached) return cached;

      const userRef = doc(db, 'users', uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const data = { id: userSnap.id, ...userSnap.data() };
        authCache.set(`userData_${uid}`, data);
        return data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching user data:', error);
      return null;
    }
  }, []);

  /**
   * Initialize user session
   */
  const initializeUserSession = useCallback(
    async firebaseUser => {
      if (!isMountedRef.current) return;

      try {
        const claims = await getCurrentUserClaims();
        setCustomClaims(claims);
        authCache.set('customClaims', claims);

        const hasAdminClaim = claims?.admin === true;
        setIsAdmin(hasAdminClaim);

        let userDoc = await fetchUserData(firebaseUser.uid);

        if (!userDoc) {
          const defaultData = getDefaultAdminData(firebaseUser, hasAdminClaim);
          const userRef = doc(db, 'users', firebaseUser.uid);
          await setDoc(userRef, defaultData);
          userDoc = { id: firebaseUser.uid, ...defaultData };
        } else {
          const userRef = doc(db, 'users', firebaseUser.uid);
          await updateDoc(userRef, {
            lastLogin: serverTimestamp(),
          }).catch(() => {});

          if (hasAdminClaim && userDoc.role !== 'admin') {
            await updateDoc(userRef, {
              role: 'admin',
              isAdmin: true,
              hasCustomClaim: true,
            });
            userDoc.role = 'admin';
            userDoc.isAdmin = true;
          }
        }

        const token = await getCurrentUserToken();
        const isValidAdmin = await verifyAdminWithBackend(token);

        const mergedUser = {
          ...firebaseUser,
          ...userDoc,
          isAdmin: hasAdminClaim && isValidAdmin,
          emailVerified: firebaseUser.emailVerified,
        };

        if (isMountedRef.current) {
          setCurrentUser(mergedUser);
          setUserData(userDoc);
          setAuthToken(token);
          authCache.set('currentUser', mergedUser);
        }

        if (tokenRefreshIntervalRef.current) {
          clearInterval(tokenRefreshIntervalRef.current);
        }

        tokenRefreshIntervalRef.current = setInterval(() => {
          const now = Date.now();
          if (now - lastTokenRefreshRef.current > 50 * 60 * 1000) {
            lastTokenRefreshRef.current = now;
            refreshAuthState();
          }
        }, 60 * 1000);
      } catch (error) {
        console.error('Error initializing user session:', error);
        throw error;
      }
    },
    [fetchUserData, verifyAdminWithBackend, refreshAuthState],
  );

  /**
   * Admin login with email and password
   */
  const login = useCallback(
    async (email, password, rememberMe = true) => {
      try {
        const persistenceType = rememberMe
          ? browserLocalPersistence
          : browserSessionPersistence;
        await setPersistence(auth, persistenceType);

        const result = await signInWithEmailAndPassword(auth, email, password);
        const user = result.user;

        const claims = await getCurrentUserClaims();

        if (!claims?.admin) {
          await signOut(auth);
          throw new Error('Access denied. Admin privileges required.');
        }

        await initializeUserSession(user);
        return user;
      } catch (error) {
        console.error('Login error:', error);
        throw error;
      }
    },
    [initializeUserSession],
  );

  /**
   * Logout
   */
  const logout = useCallback(async () => {
    try {
      if (tokenRefreshIntervalRef.current) {
        clearInterval(tokenRefreshIntervalRef.current);
        tokenRefreshIntervalRef.current = null;
      }

      await signOut(auth);
      authCache.clear();

      if (isMountedRef.current) {
        setCurrentUser(null);
        setUserData(null);
        setAuthToken(null);
        setCustomClaims(null);
        setIsAdmin(false);
      }
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  }, []);

  /**
   * Check if user has specific permission
   */
  const hasPermission = useCallback(
    permission => {
      if (!currentUser) return false;
      if (currentUser.role === 'admin') return true;
      return currentUser.permissions?.includes(permission) || false;
    },
    [currentUser],
  );

  /**
   * Auth state listener
   */
  useEffect(() => {
    let isMounted = true;

    const unsubscribe = onAuthStateChanged(auth, async firebaseUser => {
      if (!isMounted) return;

      setLoading(true);

      if (firebaseUser) {
        try {
          await initializeUserSession(firebaseUser);
        } catch (error) {
          console.error('Auth state error:', error);
          setCurrentUser(null);
          setUserData(null);
        }
      } else {
        setCurrentUser(null);
        setUserData(null);
        setAuthToken(null);
        setCustomClaims(null);
        setIsAdmin(false);
        authCache.clear();

        if (tokenRefreshIntervalRef.current) {
          clearInterval(tokenRefreshIntervalRef.current);
          tokenRefreshIntervalRef.current = null;
        }
      }

      setLoading(false);
      setAuthInitialized(true);
    });

    return () => {
      isMounted = false;
      unsubscribe();
      if (tokenRefreshIntervalRef.current) {
        clearInterval(tokenRefreshIntervalRef.current);
      }
    };
  }, [initializeUserSession]);

  const value = {
    currentUser,
    userData,
    loading,
    authToken,
    customClaims,
    isAdmin,
    authInitialized,
    login,
    logout,
    getIdToken,
    getAuthToken,
    refreshAuthState,
    hasPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
